import { FC, useState, useCallback, KeyboardEvent, useRef } from 'react'
import { Paperclip, Mic, ArrowRight, X, FileText, Loader2 } from 'lucide-react'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'
import { cn } from '../../lib/utils'
import { useAudioRecorder } from '../../hooks/useAudioRecorder'
import { useTranscription } from '../../hooks/useTranscription'
import { useChat } from '../../contexts/ChatContext'

interface ProjectChatInputProps {
  projectName: string
  onSend: (message: string) => void
}

export const ProjectChatInput: FC<ProjectChatInputProps> = ({
  projectName,
  onSend
}) => {
  const [input, setInput] = useState('')
  const [transcriptionError, setTranscriptionError] = useState<string | null>(null)
  const { isRecording, startRecording, stopRecording, audioBlob, clearAudioBlob } = useAudioRecorder()
  const { files, addFile, removeFile, transcribeAudio } = useChat()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Process transcribed audio → append to input, surface failures inline
  useTranscription({
    audioBlob,
    clearAudioBlob,
    transcribeAudio,
    onText: text => setInput(prev => prev + text),
    onError: setTranscriptionError,
  })

  // Clear any prior transcription error, then start a fresh recording
  const handleStartRecording = useCallback(() => {
    setTranscriptionError(null)
    startRecording()
  }, [startRecording])

  const handleSubmit = () => {
    if (input.trim() || files.length > 0) {
      onSend(input.trim())
      setInput('')
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      await addFile(e.target.files[0], 'paperclip')
      e.target.value = ''
    }
  }

  const hasUploadingFiles = files.some(f => f.status === 'uploading')

  return (
    <div className="w-full flex flex-col gap-2">
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        hidden
        onChange={handleFileChange}
        accept=".txt,.md,.xml,.cda,.pdf,.csv,.jpg,.jpeg,.png,text/plain,text/markdown,application/xml,application/pdf,text/csv,image/jpeg,image/png"
      />

      {/* File Badges */}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 px-1 animate-fade-in">
          {files.map(f => (
            <Badge
              key={f.id}
              variant="secondary"
              className={cn(
                "gap-2 pr-1 h-7",
                "bg-surface-elevated border border-border/50",
                "hover:border-primary/30",
                "transition-all duration-200"
              )}
            >
              {f.status === 'uploading' ? (
                <Loader2 className="h-3 w-3 animate-spin text-primary"/>
              ) : (
                <FileText className="h-3 w-3 text-primary"/>
              )}
              <span className="max-w-[120px] truncate text-foreground text-xs">{f.name}</span>
              <button
                onClick={() => removeFile(f.id)}
                className="ml-1 p-0.5 rounded hover:bg-destructive/10 hover:text-destructive transition-colors"
                aria-label={`Remove ${f.name}`}
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Input Container */}
      <div
        className={cn(
          "flex items-center gap-3 px-4 py-3 rounded-full",
          "border border-border/50 bg-surface/50",
          "focus-within:border-primary/50 focus-within:bg-surface",
          "transition-colors duration-200",
          isRecording && "border-destructive/50 bg-destructive/5"
        )}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={() => fileInputRef.current?.click()}
              className="h-8 w-8 rounded-full flex-shrink-0 text-muted-foreground hover:text-foreground hover:bg-accent/50"
              aria-label="Attach file"
            >
              <Paperclip className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs">Attach file</p>
          </TooltipContent>
        </Tooltip>

        <input
          type="text"
          value={input}
          onChange={(e) => {
            setInput(e.target.value)
            if (transcriptionError) setTranscriptionError(null)
          }}
          onKeyDown={handleKeyDown}
          placeholder={`New chat in ${projectName.length > 40 ? projectName.slice(0, 40) + '…' : projectName}`}
          className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground"
          aria-label="Chat message input"
        />

        {/* Mic button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={isRecording ? stopRecording : handleStartRecording}
          className={cn(
            "h-8 w-8 rounded-full flex-shrink-0",
            isRecording && "text-destructive animate-pulse"
          )}
          aria-label={isRecording ? 'Stop recording' : 'Voice input'}
        >
          <Mic className="h-4 w-4" />
        </Button>

        {/* Send button */}
        <Button
          variant="default"
          size="icon"
          onClick={handleSubmit}
          disabled={(!input.trim() && files.length === 0) || hasUploadingFiles}
          className="h-8 w-8 rounded-full flex-shrink-0"
          aria-label="Send message"
        >
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Transcription error */}
      {transcriptionError && (
        <p className="text-xs text-destructive mt-1 px-1">{transcriptionError}</p>
      )}
    </div>
  )
}
